import React, { useEffect, useMemo, useRef, useState } from "react";
import { CalendarOutlined, CloseOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import {
  diffNights,
  formatDateIL,
  formatDayNameIL,
  parseDDMMYYYYToLocal,
} from "../../utils/DatesHelpers";

const WEEKDAYS = ["א", "ב", "ג", "ד", "ה", "ו", "ש"];
const MONTH_NAMES = [
  "ינואר",
  "פברואר",
  "מרץ",
  "אפריל",
  "מאי",
  "יוני",
  "יולי",
  "אוגוסט",
  "ספטמבר",
  "אוקטובר",
  "נובמבר",
  "דצמבר",
];

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(12, 0, 0, 0);
  return x;
}

function sameDay(a, b) {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function isBeforeDay(a, b) {
  return startOfDay(a).getTime() < startOfDay(b).getTime();
}

function isInRange(day, start, end) {
  if (!start || !end) return false;
  const t = startOfDay(day).getTime();
  return t > startOfDay(start).getTime() && t < startOfDay(end).getTime();
}

function formatRangeSummary(checkIn, checkOut) {
  if (!checkIn) return "בחרו תאריכי שהייה";
  const inLabel = `${formatDateIL(checkIn)} ${formatDayNameIL(checkIn)}`;
  if (!checkOut) return `${inLabel} – בחרו חזרה`;
  const nights = diffNights(checkIn, checkOut);
  const nightsLabel = nights === 1 ? "לילה אחד" : `${nights} לילות`;
  const outLabel = `${formatDateIL(checkOut)} ${formatDayNameIL(checkOut)}`;
  return `${inLabel} – ${outLabel} (${nightsLabel})`;
}

function formatCompactLabel(checkIn, checkOut) {
  if (!checkIn || !checkOut) return "בחרו תאריכים";
  return `${formatDateIL(checkIn)} – ${formatDateIL(checkOut)}`;
}

function formatHebrewRangeLabel(checkIn, checkOut) {
  if (!checkIn || !checkOut) return "";
  const d1 = checkIn.getDate();
  const d2 = checkOut.getDate();
  const m1 = MONTH_NAMES[checkIn.getMonth()];
  const m2 = MONTH_NAMES[checkOut.getMonth()];
  if (checkIn.getMonth() === checkOut.getMonth() && checkIn.getFullYear() === checkOut.getFullYear()) {
    return `${d1}–${d2} ב${m1}`;
  }
  return `${d1} ב${m1} – ${d2} ב${m2}`;
}

function buildMonthCells(year, monthIndex) {
  const first = new Date(year, monthIndex, 1, 12);
  const startPad = first.getDay(); // Sunday = 0
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startPad; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) {
    cells.push(new Date(year, monthIndex, d, 12));
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function MonthGrid({ year, monthIndex, checkIn, checkOut, hoverDate, today, onPick, onHover }) {
  const cells = useMemo(() => buildMonthCells(year, monthIndex), [year, monthIndex]);
  return (
    <div className="hrm-cal__month">
      <h3 className="hrm-cal__month-title">
        {MONTH_NAMES[monthIndex]} {year}
      </h3>
      <div className="hrm-cal__weekdays" aria-hidden>
        {WEEKDAYS.map((d) => (
          <span key={d}>{d}</span>
        ))}
      </div>
      <div className="hrm-cal__grid" role="grid" aria-label={`${MONTH_NAMES[monthIndex]} ${year}`}>
        {cells.map((day, idx) => {
          if (!day) return <span key={`e-${idx}`} className="hrm-cal__cell hrm-cal__cell--empty" />;
          const disabled = isBeforeDay(day, today);
          const isStart = sameDay(day, checkIn);
          const isEnd = sameDay(day, checkOut);
          const inRange = isInRange(day, checkIn, checkOut);
          const hoverRange =
            checkIn &&
            !checkOut &&
            hoverDate &&
            isInRange(day, checkIn, hoverDate) &&
            !isBeforeDay(hoverDate, checkIn);
          const cls = [
            "hrm-cal__cell",
            disabled ? "is-disabled" : "",
            isStart || isEnd ? "is-endpoint" : "",
            isStart ? "is-start" : "",
            isEnd ? "is-end" : "",
            inRange || hoverRange ? "is-in-range" : "",
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <button
              key={day.toISOString()}
              type="button"
              className={cls}
              disabled={disabled}
              onClick={() => onPick(day)}
              onMouseEnter={() => onHover(day)}
              aria-label={formatDateIL(day)}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Compact professional date-range picker for packages results mockup.
 */
export default function MockDateRangeField({
  checkInStr,
  checkOutStr,
  onChange,
  disabled = false,
}) {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [hoverDate, setHoverDate] = useState(null);
  const [viewMonth, setViewMonth] = useState(() => {
    const base = parseDDMMYYYYToLocal(checkInStr?.split(" - ")?.[0]) || new Date();
    return { year: base.getFullYear(), month: base.getMonth() };
  });

  useEffect(() => {
    const parts = String(checkInStr || checkOutStr || "").includes(" - ")
      ? String(checkInStr).split(" - ")
      : [checkInStr, checkOutStr];
    const a = parseDDMMYYYYToLocal(parts[0]?.trim?.() || checkInStr);
    const b = parseDDMMYYYYToLocal(parts[1]?.trim?.() || checkOutStr);
    setCheckIn(a);
    setCheckOut(b);
    if (a) setViewMonth({ year: a.getFullYear(), month: a.getMonth() });
  }, [checkInStr, checkOutStr]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const today = useMemo(() => startOfDay(new Date()), []);
  const nextMonth = useMemo(() => {
    const d = new Date(viewMonth.year, viewMonth.month + 1, 1);
    return { year: d.getFullYear(), month: d.getMonth() };
  }, [viewMonth]);

  const canGoBack = useMemo(() => {
    const prev = new Date(viewMonth.year, viewMonth.month, 1);
    const floor = new Date(today.getFullYear(), today.getMonth(), 1);
    return prev > floor;
  }, [viewMonth, today]);

  const applyRange = (start, end) => {
    const dateRangeInput = `${formatDateIL(start)} - ${formatDateIL(end)}`;
    onChange?.({
      checkIn: `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, "0")}-${String(start.getDate()).padStart(2, "0")}`,
      checkOut: `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, "0")}-${String(end.getDate()).padStart(2, "0")}`,
      dateRangeInput,
      dateRangeLabel: formatHebrewRangeLabel(start, end),
    });
  };

  const onPick = (day) => {
    if (!checkIn || (checkIn && checkOut)) {
      setCheckIn(day);
      setCheckOut(null);
      return;
    }
    if (isBeforeDay(day, checkIn) || sameDay(day, checkIn)) {
      setCheckIn(day);
      setCheckOut(null);
      return;
    }
    setCheckOut(day);
    applyRange(checkIn, day);
    setOpen(false);
  };

  const shiftMonth = (delta) => {
    const d = new Date(viewMonth.year, viewMonth.month + delta, 1);
    setViewMonth({ year: d.getFullYear(), month: d.getMonth() });
  };

  const triggerLabel = formatCompactLabel(
    parseDDMMYYYYToLocal(String(checkInStr || "").split(" - ")[0]) || checkIn,
    parseDDMMYYYYToLocal(String(checkInStr || "").split(" - ")[1]) || checkOut
  );

  return (
    <div className="hrm-picker" ref={rootRef}>
      <button
        type="button"
        className={`hrm-picker__trigger${open ? " is-open" : ""}`}
        aria-expanded={open}
        aria-haspopup="dialog"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
      >
        <CalendarOutlined aria-hidden />
        <span>{triggerLabel}</span>
      </button>

      {open ? (
        <div className="hrm-cal" role="dialog" aria-label="בחירת תאריכים" dir="rtl">
          <div className="hrm-cal__head">
            <p className="hrm-cal__summary">{formatRangeSummary(checkIn, checkOut)}</p>
            <div className="hrm-cal__nav">
              <button
                type="button"
                className="hrm-cal__nav-btn"
                disabled={!canGoBack}
                aria-label="חודש אחורה"
                onClick={() => shiftMonth(-1)}
              >
                <RightOutlined />
              </button>
              <button
                type="button"
                className="hrm-cal__nav-btn"
                aria-label="חודש קדימה"
                onClick={() => shiftMonth(1)}
              >
                <LeftOutlined />
              </button>
              <button
                type="button"
                className="hrm-cal__close"
                aria-label="סגירה"
                onClick={() => setOpen(false)}
              >
                <CloseOutlined />
              </button>
            </div>
          </div>

          <div className="hrm-cal__body" onMouseLeave={() => setHoverDate(null)}>
            <MonthGrid
              year={viewMonth.year}
              monthIndex={viewMonth.month}
              checkIn={checkIn}
              checkOut={checkOut}
              hoverDate={hoverDate}
              today={today}
              onPick={onPick}
              onHover={setHoverDate}
            />
            <MonthGrid
              year={nextMonth.year}
              monthIndex={nextMonth.month}
              checkIn={checkIn}
              checkOut={checkOut}
              hoverDate={hoverDate}
              today={today}
              onPick={onPick}
              onHover={setHoverDate}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
